import React, { useMemo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { styled } from '@mui/material'
import { VersionReportPageType } from '../../../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { PageThumbnailMenuButton } from './PageThumbnailMenuButton'
import { VersionReportTemplatePageFormModel } from '../../../model/VersionReportTemplatePageFormModel'

const PAGE_TYPE_INFO_MAP: Record<
  VersionReportPageType,
  { color: string; label: string }
> = {
  PROGRESS_OVERVIEW: {
    color: colorPalette.green[5],
    label: '進捗概況',
  },
  PROGRESS_REPORT_BY_PROCESS: {
    color: colorPalette.blue[7],
    label: '工程別進捗',
  },
  PROGRESS_REPORT_BY_TEAM: {
    color: colorPalette.yellow[4],
    label: 'チーム別進捗',
  },
}

const getPageTypeInfo = (pageType: VersionReportPageType) => {
  return (
    PAGE_TYPE_INFO_MAP[pageType] || {
      color: colorPalette.monotone[4],
      label: 'その他',
    }
  )
}

interface PageThumbnailItemProps {
  page: VersionReportTemplatePageFormModel
  isSelected: boolean
  hasError: boolean
  onSelect: () => void
  onDelete: () => void
}

export const PageThumbnailItem: React.FC<PageThumbnailItemProps> = ({
  page,
  isSelected,
  hasError,
  onSelect,
  onDelete,
}) => {
  const pageInfo = getPageTypeInfo(page.pageType)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.pageNumber,
  })

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition]
  )

  return (
    <ThumbnailItem
      ref={setNodeRef}
      style={style}
      isSelected={isSelected}
      hasError={hasError}
      isDragging={isDragging}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <PageNumberBadge color={pageInfo.color}>
        {page.pageNumber}
      </PageNumberBadge>

      <ThumbnailContent isSelected={isSelected}>
        <PageTitle>{page.pageTitle}</PageTitle>
        <PageTypeLabel>{pageInfo.label}</PageTypeLabel>
      </ThumbnailContent>

      <MenuButtonWrapper
        onClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      >
        <PageThumbnailMenuButton onDelete={onDelete} />
      </MenuButtonWrapper>
    </ThumbnailItem>
  )
}

const ThumbnailItem = styled('div')<{
  isSelected: boolean
  hasError: boolean
  isDragging?: boolean
}>(({ isSelected, hasError, isDragging }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '120px',
  cursor: isDragging ? 'grabbing' : 'grab',
  border: hasError
    ? `2px solid ${colorPalette.pink[5]}`
    : `2px solid ${isSelected ? colorPalette.skyBlue[7] : colorPalette.monotone[2]}`,
  borderRadius: '8px',
  backgroundColor: isSelected
    ? colorPalette.skyBlue[7]
    : colorPalette.monotone[0],
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  opacity: isDragging ? 0.5 : 1,
  zIndex: isDragging ? 1000 : 'auto',

  '&:hover': {
    borderColor: hasError ? colorPalette.pink[5] : colorPalette.skyBlue[7],
    backgroundColor: isSelected
      ? colorPalette.skyBlue[7]
      : colorPalette.monotone[1],
  },

  '&:hover .menu-button': {
    opacity: 1,
  },
}))

const PageNumberBadge = styled('div')<{ color: string }>(({ color }) => ({
  position: 'absolute',
  top: '-8px',
  left: '8px',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: color,
  color: colorPalette.monotone[0],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
  zIndex: 1,
  pointerEvents: 'none',
}))

const ThumbnailContent = styled('div')<{ isSelected: boolean }>(
  ({ isSelected }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 8px',
    gap: '8px',
    color: isSelected ? colorPalette.monotone[0] : colorPalette.monotone[10],
    pointerEvents: 'none',
  })
)

const PageTypeLabel = styled('div')({
  fontSize: '10px',
  fontWeight: 400,
  textAlign: 'center',
  opacity: 0.7,
})

const PageTitle = styled('div')({
  fontSize: '14px',
  fontWeight: 'bold',
  textAlign: 'center',
  wordBreak: 'break-all',
  lineHeight: 1.2,
  maxHeight: '2.4em',
  overflow: 'hidden',
})

const MenuButtonWrapper = styled('div')({
  position: 'absolute',
  top: '4px',
  right: '4px',
  pointerEvents: 'auto',
})
